/**
 * Onglet « Éditions » — chaque modèle (Composer) et chaque traduction activée (Traductions)
 * donne une carte en ligne dédiée : lien web unique, QR code et PDF. En-tête : page au nom
 * de la cave avec le lien unique du portail, son QR et le choix de la carte affichée par défaut.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, QrCode, ExternalLink, FileDown, Copy, Check } from 'lucide-react';
import { carteApi } from '../../services/api';
import { Spinner, Card } from '../../components/ui/kit';

const LANGS = { fr: 'Français', en: 'English', nl: 'Nederlands', de: 'Deutsch', es: 'Español' };

function CopyBtn({ text, testid, label = 'Copier le lien' }) {
  const [done, setDone] = useState(false);
  return (
    <button className="vd-btn vd-btn-ghost h-8 text-[12px]" data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success('Lien copié'); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {done ? 'Copié ✓' : label}
    </button>
  );
}

const cardLabel = (e) => (e.is_source ? e.template_name : `${e.template_name} — ${(LANGS[e.lang] || e.lang).toUpperCase()}`);

export default function Editions() {
  const { data: editions = [], isLoading } = useQuery({ queryKey: ['carte-editions'], queryFn: carteApi.editions });
  const { data: portalDef, refetch: refetchPortal } = useQuery({ queryKey: ['carte-portal-default'], queryFn: carteApi.portalDefault });

  const slug = editions[0]?.slug;
  const { data: portalInfo } = useQuery({ queryKey: ['carte-portal-info', slug], queryFn: () => carteApi.portalLanguages(slug), enabled: !!slug });
  const caveName = portalInfo?.establishment || 'Cave';
  const portalUrl = slug ? `${window.location.origin}/c/${slug}` : null;

  const defaultValue = (portalDef?.template_id && portalDef?.lang)
    ? `${portalDef.template_id}|${portalDef.lang}`
    : (editions[0] ? `${editions[0].template_id}|${editions[0].lang}` : '');

  const setDefault = async (value) => {
    const [tid, lang] = value.split('|');
    await carteApi.setPortalDefault(tid, lang);
    await refetchPortal();
    toast.success('Carte affichée par défaut mise à jour');
  };

  // Éditions sélectionnables en onglet (modèles ET versions traduites), clé = template_id|lang.
  const defaultKey = defaultValue;
  const otherEditions = editions.filter((e) => `${e.template_id}|${e.lang}` !== defaultKey);
  const tabKeys = (portalDef?.tab_ids || []).filter((k) => k !== defaultKey);

  const toggleTab = async (key) => {
    const next = tabKeys.includes(key) ? tabKeys.filter((x) => x !== key) : [...tabKeys, key];
    await carteApi.setPortalTabs(next);
    await refetchPortal();
    toast.success('Onglets du portail mis à jour');
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;
  if (!editions.length) return <div className="p-6"><p className="text-[13px] text-n600">Créez un modèle dans « Composer » pour obtenir une carte en ligne.</p></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] space-y-6">
      {/* En-tête : portail unique de la cave */}
      <Card className="p-6" data-testid="portal-card">
        <div className="flex items-center gap-2 mb-1"><Globe className="w-4 h-4 text-gold700" /><span className="kicker">Carte en ligne — lien unique</span></div>
        <p className="font-title text-2xl mb-4" data-testid="portal-cave-name">{caveName}</p>
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          <img src={carteApi.qrPngUrl(slug)} alt="QR portail" className="w-32 h-32 border border-hair shrink-0" data-testid="portal-qr-img" />
          <div className="flex-1 min-w-0 space-y-3">
            <div>
              <span className="kicker block mb-1">Lien unique</span>
              <div className="flex flex-wrap items-center gap-2">
                <a href={portalUrl} target="_blank" rel="noreferrer" className="vd-link text-[13px] break-all" data-testid="portal-link">{portalUrl}</a>
                <CopyBtn text={portalUrl} testid="portal-copy" />
                <button className="vd-btn vd-btn-ghost h-8 text-[12px]" onClick={() => window.open(portalUrl, '_blank')} data-testid="portal-open"><ExternalLink className="w-3.5 h-3.5" /> Ouvrir</button>
                <button className="vd-btn vd-btn-ghost h-8 text-[12px]" onClick={() => carteApi.downloadQr('png', slug)} data-testid="portal-qr-dl"><QrCode className="w-3.5 h-3.5" /> QR PNG</button>
              </div>
            </div>
            <label className="block">
              <span className="kicker block mb-1">Carte affichée par défaut</span>
              <select className="vd-input h-9 min-w-[280px]" value={defaultValue} onChange={(e) => setDefault(e.target.value)} data-testid="portal-default-select">
                {editions.map((e) => <option key={`${e.template_id}|${e.lang}`} value={`${e.template_id}|${e.lang}`}>{cardLabel(e)}</option>)}
              </select>
            </label>
            {otherEditions.length > 0 && (
              <div data-testid="portal-tabs-picker">
                <span className="kicker block mb-1.5">Autres cartes affichées en onglet sur le portail</span>
                <div className="flex flex-col gap-1.5">
                  {otherEditions.map((e) => {
                    const key = `${e.template_id}|${e.lang}`;
                    return (
                      <label key={key} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`portal-tab-${key}`}>
                        <input type="checkbox" className="accent-gold700 w-4 h-4" checked={tabKeys.includes(key)}
                          onChange={() => toggleTab(key)} data-testid={`portal-tab-check-${key}`} />
                        <span>{cardLabel(e)}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11.5px] text-n500 mt-1.5">Chaque carte cochée (modèle ou version traduite) apparaît comme un onglet en haut du portail unique, à côté de la carte par défaut.</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <p className="text-[12.5px] text-n600">Toujours à jour : chaque changement de stock, de prix ou de modèle régénère automatiquement les PDF, les cartes web et les QR codes. Les liens ne changent jamais, les QR imprimés restent valables.</p>

      {/* Une carte en ligne par modèle × langue */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="editions-list">
        {editions.map((e) => {
          const url = `${window.location.origin}/c/${e.slug}?m=${e.template_id}&lang=${e.lang}`;
          const key = `${e.template_id}-${e.lang}`;
          return (
            <Card key={key} className="p-4 flex flex-col" data-testid={`edition-${key}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-title text-lg leading-tight" data-testid={`edition-title-${key}`}>{cardLabel(e)}</p>
                <span className="vd-tag vd-tag-accent text-[11px] shrink-0">{LANGS[e.lang] || e.lang}</span>
              </div>
              <img src={carteApi.qrPngUrl(slug, e.template_id, e.lang)} alt="QR" className="w-28 h-28 border border-hair mb-3" data-testid={`edition-qr-${key}`} />
              <a href={url} target="_blank" rel="noreferrer" className="vd-link text-[12px] break-all mb-3" data-testid={`edition-url-${key}`}>{url}</a>
              <div className="mt-auto flex flex-wrap gap-2">
                <button className="vd-btn vd-btn-primary h-8 text-[12px]" onClick={() => carteApi.generate(e.template_id, e.lang, e.template_name)} data-testid={`edition-pdf-${key}`}><FileDown className="w-3.5 h-3.5" /> PDF</button>
                <button className="vd-btn vd-btn-ghost h-8 text-[12px]" onClick={() => window.open(url, '_blank')} data-testid={`edition-open-${key}`}><ExternalLink className="w-3.5 h-3.5" /> Carte web</button>
                <CopyBtn text={url} testid={`edition-copy-${key}`} label="Lien" />
                <button className="vd-btn vd-btn-ghost h-8 text-[12px]" onClick={() => carteApi.downloadQr('png', slug, e.template_id, e.lang)} data-testid={`edition-qrdl-${key}`}><QrCode className="w-3.5 h-3.5" /> QR</button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
